import config from 'config/common';

interface BrazeBanner {
    isControl: boolean;
}

interface BrazeGlobal {
    getBanner(placementId: string): BrazeBanner | null | undefined;
    insertBanner(banner: BrazeBanner, container: HTMLElement): void;
    subscribeToBannersUpdates(callback: () => void): void;
    requestBannersRefresh(placementIds: string[]): void;
}

declare global {
    interface Window {
        braze?: BrazeGlobal;
    }
}

const SDK_POLL_INTERVAL = 200;
const SDK_POLL_TIMEOUT = 10_000;
const REFRESH_RESPONSE_TIMEOUT = 5_000;

class BrazeService {
    private sdk: BrazeGlobal | null = null;
    private readyPromise: Promise<BrazeGlobal | null> | null = null;
    private bannerListeners: Array<() => void> = [];
    // Braze SDK allows only one requestBannersRefresh per session; we fire once with all placements.
    private refreshFired = false;
    private refreshResponded = false;

    waitForSdk(): Promise<BrazeGlobal | null> {
        if (this.readyPromise) return this.readyPromise;
        if (!config.brazeBanner.isEnabled) return Promise.resolve(null);

        this.readyPromise = new Promise<BrazeGlobal | null>((resolve) => {
            if (window.braze) {
                this.setup(window.braze);
                return resolve(window.braze);
            }

            let elapsed = 0;
            const timer = setInterval(() => {
                elapsed += SDK_POLL_INTERVAL;
                if (window.braze) {
                    clearInterval(timer);
                    this.setup(window.braze);
                    resolve(window.braze);
                } else if (elapsed >= SDK_POLL_TIMEOUT) {
                    clearInterval(timer);
                    this.readyPromise = null;
                    console.warn('[Braze] SDK not found on window after timeout');
                    resolve(null);
                }
            }, SDK_POLL_INTERVAL);
        });

        return this.readyPromise;
    }

    private setup(braze: BrazeGlobal): void {
        this.sdk = braze;

        braze.subscribeToBannersUpdates(() => {
            if (this.refreshFired) this.refreshResponded = true;
            this.bannerListeners.forEach((fn) => fn());
        });
    }

    getBanner(placementId: string): BrazeBanner | null | undefined {
        return this.sdk?.getBanner(placementId);
    }

    insertBanner(banner: BrazeBanner, container: HTMLElement): void {
        this.sdk?.insertBanner(banner, container);
    }

    refreshBanners(placementIds: string[]): void {
        if (!this.sdk || this.refreshFired) return;

        const ids = new Set<string>(placementIds);
        if (config.brazeBanner.placementId) ids.add(config.brazeBanner.placementId);
        if (config.brazeBanner.desktopPlacementId) ids.add(config.brazeBanner.desktopPlacementId);
        if (ids.size === 0) return;

        this.refreshFired = true;
        this.sdk.requestBannersRefresh(Array.from(ids));

        // Braze doesn't fire an update event for users with no banners, and re-refreshes
        // within the same session are rejected silently. Mark the refresh as responded
        // after a short wait so components stop waiting and hide their skeletons.
        window.setTimeout(() => {
            if (this.refreshResponded) return;
            this.refreshResponded = true;
            this.bannerListeners.forEach((fn) => fn());
        }, REFRESH_RESPONSE_TIMEOUT);
    }

    onBannersUpdated(listener: () => void): () => void {
        this.bannerListeners.push(listener);
        // Always fire on subscribe so late-mounting components can read banners the SDK
        // already cached (e.g. on page refresh, where our own refresh call is blocked by
        // Braze's once-per-session rule and no fresh event will arrive).
        if (this.sdk) listener();
        return () => {
            this.bannerListeners = this.bannerListeners.filter((fn) => fn !== listener);
        };
    }

    hasRefreshResponded(): boolean {
        return this.refreshResponded;
    }

    reset(): void {
        this.sdk = null;
        this.readyPromise = null;
        this.bannerListeners = [];
        this.refreshFired = false;
        this.refreshResponded = false;
    }
}

export const brazeService = new BrazeService();
